/**
 * Vendor Routes Root
 *
 * Purpose:
 * - Hosts unified vendor routes (list, orders, invoices, and wrappers).
 * - Normalizes base /vendor path and wires RBAC for nested components.
 */
import React, { useEffect } from "react";
import { useRoutes, useNavigate, useLocation } from "react-router-dom";
import { withRouter } from "../../util/with-router";
import { TransitionGroup } from "react-transition-group";
import VendorList from './vendor-list';
import VendorOrders from './orders';
import VendorInvoices from './invoices';
import InvoiceManagement from '../suppliers/invoice-management/InvoiceManagement';
import RBACApplicationAuth from "../../layout/rbac";
import * as paths from "../../constants/route-paths";

// Existing components for sub-routes
import ViewPurchaseOrderWrapper from './view-purchase-order-wrapper';
import ViewWorkOrderWrapper from './view-work-order-wrapper';
import ViewInvoiceSupplierWrapper from './view-invoice-supplier-wrapper';
import ViewInvoiceContractorWrapper from './view-invoice-contractor-wrapper';
import EditPurchaseOrderWrapper from './edit-purchase-order-wrapper';
import EditWorkOrderWrapper from './edit-work-order-wrapper';
import CreatePurchaseOrderWrapper from './create-purchase-order-wrapper';
import CreateWorkOrderWrapper from './create-work-order-wrapper';

// Wrapper components for vendor context
import AddSupplierWrapper from './add-supplier-wrapper';
import AddContractorWrapper from './add-contractor-wrapper';
import ImportVendors from '../../components/bulk-vendor-import';

function CustomRoutes({ location }) {
    const navigate = useNavigate();
    const currentLocation = useLocation();

    // Handle base /vendor path redirect
    useEffect(() => {
        if (currentLocation.pathname === '/vendor' || currentLocation.pathname === '/vendor/') {
            navigate(`/${paths.VENDOR}/${paths.VENDOR_LIST}`, { replace: true });
        }
    }, [currentLocation.pathname, navigate]);

    let element = useRoutes([
        {
            path: paths.VENDOR_LIST,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={VendorList} />,
        },
        {
            path: paths.VENDOR_ORDERS,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={VendorOrders} />,
        },
        {
            path: `${paths.VENDOR_ORDERS}/${paths.VIEW_PURCHASE_ORDER}`,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={ViewPurchaseOrderWrapper} />,
        },
        {
            path: `${paths.VENDOR_ORDERS}/${paths.CREATE_PURCHASE_ORDER}`,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={CreatePurchaseOrderWrapper} />,
        },
        {
            path: `${paths.VENDOR_ORDERS}/${paths.EDIT_PURCHASE_ORDER}`,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={EditPurchaseOrderWrapper} />,
        },
        {
            path: `${paths.VENDOR_ORDERS}/${paths.VIEW_WORK_ORDER}`,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={ViewWorkOrderWrapper} />,
        },
        {
            path: `${paths.VENDOR_ORDERS}/${paths.CREATE_WORK_ORDER}`,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={CreateWorkOrderWrapper} />,
        },
        {
            path: `${paths.VENDOR_ORDERS}/${paths.EDIT_WORK_ORDER}`,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={EditWorkOrderWrapper} />,
        },
        {
            path: paths.VENDOR_INVOICES,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={VendorInvoices} />,
        },
        {
            path: paths.VENDOR_INVOICE_MANAGEMENT,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={InvoiceManagement} />,
        },
        {
            path: `${paths.VENDOR_INVOICES}/${paths.VENDOR_SUPPLIER_INVOICE_VIEW}`,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={ViewInvoiceSupplierWrapper} />,
        },
        {
            path: `${paths.VENDOR_INVOICES}/${paths.VENDOR_CONTRACTOR_INVOICE_VIEW}`,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={ViewInvoiceContractorWrapper} />,
        },
        {
            path: paths.VENDOR_ADD_SUPPLIER,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={AddSupplierWrapper} />,
        },
        {
            path: `${paths.VENDOR_ADD_SUPPLIER}/${paths.EDIT_SUPPLIER}`,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={AddSupplierWrapper} />,
        },
        {
            path: paths.VENDOR_ADD_CONTRACTOR,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={AddContractorWrapper} />,
        },
        {
            path: `${paths.VENDOR_ADD_CONTRACTOR}/${paths.EDIT_CONTRACTOR}`,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={AddContractorWrapper} />,
        },
        {
            path: paths.VENDOR_IMPORT_SUPPLIERS,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={ImportVendors} />,
        },
        {
            path: paths.VENDOR_IMPORT_CONTRACTORS,
            element: <RBACApplicationAuth moduleURL={location.pathname} component={ImportVendors} />,
        },
    ]);

    return element;
}

class Root extends React.PureComponent {
    render() {
        const { location } = this.props;
        return (
            <div className="clearfix" style={{ width: "100%" }}>
                <TransitionGroup>
                    <CustomRoutes location={location} />
                </TransitionGroup>
            </div>
        );
    }
}
export default withRouter(Root);
